import { deliveryFeeDisplay } from 'src/utils/deliveryFeeDisplay'
import React from 'react'
import { useSelector } from 'react-redux'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import { Button, Table, Tag, Tooltip, Typography } from 'antd'
import styled from 'styled-components/macro'
import FadeIn from 'src/components/styled/FadeIn'
import { CheckCircleOutlined, InfoCircleOutlined } from '@ant-design/icons'
import GrowthEngineOrderSuccessCard from 'src/components/growthEngine/GrowthEngineOrderSuccessCard'

const { Title, Text } = Typography

const Wrapper = styled(FadeIn)`
  padding: 24px;
  max-width: 1200px;
  margin: 0 auto;
`

const Header = styled.div`
  text-align: center;
  margin-bottom: 32px;
`

const SuccessIcon = styled(CheckCircleOutlined)`
  font-size: 64px;
  color: #52c41a;
  margin-bottom: 16px;
`

const SupplierSection = styled.div`
  margin-bottom: 40px;
  background: #fff;
  border-radius: 8px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`

const SupplierHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 16px;
  border-bottom: 2px solid #f0f0f0;
`

const SupplierTitle = styled(Title)`
  margin: 0 !important;
  font-size: 20px !important;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
`

const SupplierTitleBadge = styled(Tag)`
  margin: 0;
`

const SupplierTotal = styled.div`
  text-align: right;
`

const TotalLabel = styled(Text)`
  font-size: 14px;
  color: #666;
  display: block;
  margin-bottom: 4px;
`

const TotalAmount = styled(Text)`
  font-size: 20px;
  font-weight: 600;
  color: #1890ff;
`

const Actions = styled.div`
  display: flex;
  justify-content: center;
  gap: 16px;
  margin-top: 32px;
`

const OrderSuccessScreen = () => {
  const history = useHistory()
  const location = useLocation()
  const { venueId } = useParams()
  const { currentPlan } =
    useSelector(state => state.venue?.venue?.usageLimits) ?? {}

  // Get order data from location state (passed from CheckoutScreen) or sessionStorage (after refresh)
  const orderData = React.useMemo(
    () =>
      location.state?.orderData ||
      JSON.parse(sessionStorage.getItem('orderSuccessData') || 'null'),
    [location.state?.orderData],
  )
  const selectedSuppliers = React.useMemo(
    () =>
      location.state?.selectedSuppliers ||
      JSON.parse(sessionStorage.getItem('orderSuccessSuppliers') || '[]'),
    [location.state?.selectedSuppliers],
  )
  const confirmResult = React.useMemo(
    () =>
      location.state?.result ||
      JSON.parse(sessionStorage.getItem('orderSuccessResult') || 'null'),
    [location.state?.result],
  )

  // Note: Refresh removed per user request - basket clearing handled elsewhere

  // If no data, redirect back to checkout
  if (!orderData) {
    React.useEffect(() => {
      history.replace(`/v/${venueId}/checkout`)
    }, [history, venueId])
    return null
  }

  const columns = [
    {
      title: 'Product',
      dataIndex: 'productTitle',
      key: 'productTitle',
      render: (text, record) => (
        <div>
          <div style={{ fontWeight: 500 }}>{text || 'Unknown Product'}</div>
          {record.sku && (
            <Text type='secondary' style={{ fontSize: '12px' }}>
              SKU: {record.sku}
            </Text>
          )}
        </div>
      ),
    },
    {
      title: 'Quantity',
      dataIndex: 'quantity',
      key: 'quantity',
      align: 'center',
      width: 100,
    },
    {
      title: 'Price',
      dataIndex: 'price',
      key: 'price',
      align: 'right',
      width: 120,
      render: (price, record) => {
        const formattedPrice = `£${(price || 0).toFixed(2)}`
        return (
          <div>
            <div>{formattedPrice}</div>
            {record.hasVat && (
              <Tag color='blue' style={{ fontSize: '11px', marginTop: '4px' }}>
                Inc VAT
              </Tag>
            )}
          </div>
        )
      },
    },
    {
      title: 'Total',
      dataIndex: 'total',
      key: 'total',
      align: 'right',
      width: 120,
      render: total => `£${(total || 0).toFixed(2)}`,
    },
  ]

  // Filter suppliers to only show selected ones
  const displayedSuppliers = orderData.suppliers
    .filter(supplier => selectedSuppliers.includes(supplier.supplierId))
    .map(supplier => ({
      ...supplier,
      orderFinancialSnapshot: confirmResult?.suppliers?.find(
        s => s.supplierId === supplier.supplierId,
      )?.orderFinancialSnapshot,
    }))

  const apiSuppliers = confirmResult?.suppliers || []
  const supplierMap = new Map(apiSuppliers.map(s => [s.supplierId, s]))
  const supplierCountFromApi = confirmResult?.supplierCount
  const restrictedCountFromApi = apiSuppliers.filter(
    s => s.restricted === true,
  ).length

  const isSupplierRestricted = supplierId =>
    supplierMap.get(supplierId)?.restricted === true

  const hasRestrictedSuppliers =
    Boolean(confirmResult?.hasRestrictedSuppliers) ||
    restrictedCountFromApi > 0 ||
    displayedSuppliers.some(s => isSupplierRestricted(s.supplierId))

  const restrictedSupplierCount = hasRestrictedSuppliers
    ? restrictedCountFromApi ||
      displayedSuppliers.filter(s => isSupplierRestricted(s.supplierId)).length
    : 0

  const totalSupplierCount =
    typeof supplierCountFromApi === 'number'
      ? supplierCountFromApi
      : displayedSuppliers.length

  // Calculate grand total
  const grandTotal = displayedSuppliers.reduce(
    (sum, supplier) => sum + deliveryFeeDisplay(supplier).total,
    0,
  )

  return (
    <Wrapper>
      <Header>
        <SuccessIcon />
        <Title level={2} style={{ marginBottom: 8 }}>
          Order Confirmed Successfully!
        </Title>
        <Text type='secondary' style={{ fontSize: '16px' }}>
          {hasRestrictedSuppliers ? (
            <>
              Your order is awaiting manager approval for{' '}
              {restrictedSupplierCount} supplier
              {restrictedSupplierCount !== 1 ? 's' : ''}.
              {totalSupplierCount - restrictedSupplierCount > 0 && (
                <>
                  {' '}
                  {totalSupplierCount - restrictedSupplierCount} supplier
                  {totalSupplierCount - restrictedSupplierCount !== 1
                    ? 's'
                    : ''}{' '}
                  have been sent.
                </>
              )}
            </>
          ) : (
            <>
              Your order has been sent to {totalSupplierCount} supplier
              {totalSupplierCount !== 1 ? 's' : ''}
            </>
          )}
        </Text>
      </Header>

      <GrowthEngineOrderSuccessCard
        venueId={venueId}
        orderData={orderData}
        currentPlan={currentPlan}
      />

      {displayedSuppliers.map(supplier => (
        <SupplierSection key={supplier.supplierId}>
          <SupplierHeader>
            <SupplierTitle level={4}>
              <span>{supplier.supplierTitle}</span>
              {isSupplierRestricted(supplier.supplierId) && (
                <Tooltip
                  title='This order has been sent to your manager for approval and will not be sent to the supplier until they approve it.'
                  placement='top'
                >
                  <SupplierTitleBadge color='orange'>
                    Restricted – manager approval required{' '}
                    <InfoCircleOutlined style={{ marginLeft: 4 }} />
                  </SupplierTitleBadge>
                </Tooltip>
              )}
            </SupplierTitle>
            <SupplierTotal>
              <TotalLabel>
                Goods (ex VAT): £{deliveryFeeDisplay(supplier).goods.toFixed(2)}
              </TotalLabel>
              <TotalLabel>
                Delivery (ex VAT): £
                {deliveryFeeDisplay(supplier).delivery.toFixed(2)}
              </TotalLabel>
              <TotalLabel>Order total (ex VAT)</TotalLabel>
              <TotalAmount>
                £{deliveryFeeDisplay(supplier).total.toFixed(2)}
              </TotalAmount>
            </SupplierTotal>
          </SupplierHeader>

          <Table
            columns={columns}
            dataSource={supplier.items}
            rowKey='orderId'
            pagination={false}
            size='middle'
          />
        </SupplierSection>
      ))}

      <SupplierSection style={{ background: '#f5f5f5' }}>
        <SupplierHeader>
          <SupplierTitle level={4}>Grand Total</SupplierTitle>
          <SupplierTotal>
            <TotalAmount style={{ fontSize: '24px' }}>
              £{grandTotal.toFixed(2)}
            </TotalAmount>
          </SupplierTotal>
        </SupplierHeader>
      </SupplierSection>

      <Actions>
        <Button
          type='primary'
          size='large'
          onClick={() => history.push(`/v/${venueId}/purchasing/orders`)}
        >
          View All Orders
        </Button>
        <Button
          size='large'
          onClick={() => history.push(`/v/${venueId}/products`)}
        >
          Place More Orders
        </Button>
      </Actions>
    </Wrapper>
  )
}

export default OrderSuccessScreen
